import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { compose } from 'redux';
import WebFont from 'webfontloader';
import { customization, dialogs, globals, i18next, template } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common';
import constants from '../constants';
import helpers from '../helpers';
import actions from '../actions';
import DesignFontsDialog from '../components/design-fonts-dialog';
import dialogTypes from '../../dialogs/constants/dialog-types';
import fontSettingsSourceIds from '../constants/font-settings-source-ids';
import useFontsData from '../helpers/use-fonts-data';

const {
  createMapById,
  composeFontsAssetsPreviewUrl,
  getFontsConfig,
  setCurrentFont,
  getSelectorByGlobalId,
  sortPresets,
  readOnlyModeDecorator,
  updateCustomFontPairs,
  linkFont,
  getCompiledFontPairConfig,
  applyFontPalette,
} = helpers;

const {
  constants: { events },
  trackers: { trackEvent },
} = segment;

const { GLOBAL_HEADER_CODE, DEFAULT_OFFSET_X, DEFAULT_OFFSET_Y } = constants;
const DIALOG_CLASS_NAME = 'ws-design-fonts-dialog';

const getCaptions = () => ({
  breadcrumbs: [i18next.t('Design'), i18next.t('Fonts')],
  title: i18next.t('Select a font pair'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
  description: i18next.t(
    'Switching fonts updates the appearance of all text on your site for consistent look and feel'
  ),
  searchPlaceholder: i18next.t('Search...'),
  emptyStateTitle: i18next.t('Sorry!'),
  emptyStateSubtitle: i18next.t('Nothing was found'),
  customPairsTitle: i18next.t('Custom font pairs'),
  libraryTitle: i18next.t('Library'),
  deleteLabel: i18next.t('Delete'),
  editLabel: i18next.t('Settings'),
});

const DesignFontsContainer = ({
  onDialogMainAction,
  onDialogCancel,
  getSharedData,
  resolveSharedData,
  sourceId,
}) => {
  const {
    customFonts: updatedCustomFonts,
    activeFontPairId: sharedActiveFontPair,
    initialFontPairId: sharedInitialFontPairId,
    fontPairAdjusted = false,
    offsetX = DEFAULT_OFFSET_X,
    offsetY = DEFAULT_OFFSET_Y,
  } = getSharedData();

  const { data: fontsData, isSuccess } = useFontsData();
  const isReady = isSuccess && fontsData.length > 0;

  const fonts = useSelector(customization.selectors.getFontPairs);
  const initialCustomFonts = useSelector(customization.selectors.getCustomFontPairs);
  const customFontsTemplate = useSelector(customization.selectors.getFontPairTemplate);
  const currentFontPair = useSelector(customization.accessors.getCurrentFontPair);
  const fontsVariable = useSelector((state) =>
    template.selectors.getVariableValueByPath(state, customization.constants.FONTS_VARIABLE)
  );
  const initialFontPairId = sharedInitialFontPairId || currentFontPair?.id;

  const [activeFontPairId, setActiveFontPairId] = useState(
    sharedActiveFontPair || initialFontPairId
  );
  const [customFonts, setCustomFonts] = useState(updatedCustomFonts || initialCustomFonts);
  const [dialogPosition, setDialogPosition] = useState({
    offsetX,
    offsetY,
  });

  const allFonts = useMemo(() => [...fonts, ...customFonts], [fonts, customFonts]);
  const preloadedFonts = useRef({});

  const dispatch = useDispatch();

  const fontsMap = useMemo(() => createMapById(allFonts), [allFonts]);
  const sortedFonts = useMemo(
    () => sortPresets(fonts, activeFontPairId),
    [fonts, activeFontPairId]
  );
  const sortedCustomFonts = useMemo(
    () => sortPresets(customFonts, activeFontPairId),
    [customFonts, activeFontPairId]
  );

  const [featureFlags] = useFeatureFlags(['add_custom_font_pair', 'advanced_font_customization']);
  const {
    add_custom_font_pair: isAddCustomFontPairFeatureFlagEnabled,
    advanced_font_customization: isAdvancedFontCustomizationFeatureFlagEnabled,
  } = featureFlags;

  const captions = getCaptions();

  const changeFontPairs = () => dispatch(actions.changeFontPairs());

  const preLoadFonts = useCallback(
    (fontsId) => {
      const config = getFontsConfig(fontsId, allFonts);
      const currentFont = allFonts.find((font) => font.id === fontsId);
      let compiledCurrentFont = currentFont;

      if (currentFont.isCustom) {
        compiledCurrentFont = getCompiledFontPairConfig(currentFont);
      }

      const {
        variables: { usedFonts },
      } = compiledCurrentFont;

      if (!usedFonts) {
        return;
      }

      if (!preloadedFonts.current[fontsId]) {
        WebFont.load(config);
      }

      preloadedFonts.current[fontsId] = true;
    },
    [allFonts]
  );

  const applyFontPaletteToSite = (params) =>
    applyFontPalette({
      ...params,
      allFonts,
      currentFontPair,
      fontsVariable,
      customFontsTemplate,
    });

  const onClose = () => {
    dialogs.operations.hide();
  };

  const handleCancel = () => {
    if (fontPairAdjusted) {
      const initialfontsMap = createMapById([...fonts, ...initialCustomFonts]);
      applyFontPaletteToSite({ fontPairConfig: initialfontsMap[initialFontPairId] });
    } else if (initialFontPairId !== activeFontPairId) {
      applyFontPaletteToSite({ fontPairId: initialFontPairId });
    }

    onDialogCancel({
      sourceId,
    });

    onClose();
  };

  const handleSubmit = (event) => {
    updateCustomFontPairs(initialCustomFonts, customFonts);

    const updatedFontPairs = customization.helpers.getCustomFontPairsToUpdate(
      initialCustomFonts,
      customFonts
    );

    const isFontChanged = initialFontPairId !== activeFontPairId;

    if (isFontChanged || !!updatedFontPairs.length) {
      globals.operations.changeGlobalContent(
        GLOBAL_HEADER_CODE,
        (global) => {
          const transformations = [];

          const font = fontsMap[activeFontPairId];
          let fontConfig = font;

          if (font.isCustom) {
            fontConfig = getCompiledFontPairConfig(font);
          }

          const fontSelector = getSelectorByGlobalId(fontsVariable, GLOBAL_HEADER_CODE);
          const setCurrentFontCurry = (globalItem) =>
            setCurrentFont(globalItem, fontConfig, customFontsTemplate, fontSelector);
          transformations.push(setCurrentFontCurry);

          return compose(...transformations)(global);
        },
        false
      );

      changeFontPairs();
    }

    onDialogMainAction({
      triggerId: !event ? DIALOG_BACKGROUND_TRIGGER_ID : SUBMIT_BUTTON_TRIGGER_ID,
      sourceId,
      fontsAdjusted: isFontChanged,
      ...(isFontChanged && { fontPreset: activeFontPairId }),
    });
    onClose();
  };

  const updateSharedState = (customData) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      initialFontPairId,
      ...customData,
    });
  };

  const onChangeFonts = (fontPairId) => {
    setActiveFontPairId(fontPairId);
    applyFontPaletteToSite({ fontPairId });
    updateSharedState({ activeFontPairId: fontPairId });

    trackEvent(events.DESIGN_SETTINGS_FONT_PRESET_CLICKED, {
      fontPreset: fontPairId,
    });
  };

  const onAddFontPairClick = () => {
    updateSharedState({
      activeFontPairId,
      customFonts,
      allFonts,
      applyCustomFontPair: (params) => applyFontPaletteToSite(params),
    });

    dialogs.operations.show(dialogTypes.ADD_FONT_PAIR_DIALOG, {});
  };

  const onEditFontPairClick = (id) => {
    updateSharedState({
      activeFontPairId,
      customFonts,
      allFonts,
      applyCustomFontPair: (params) => applyFontPaletteToSite(params),
    });

    dialogs.operations.show(dialogTypes.DESIGN_SETTINGS_FONT_SETTINGS_DIALOG, {
      fontPairId: id,
      sourceId: fontSettingsSourceIds.DESIGN_SETTINGS,
    });
  };

  const onDeleteFontPairClick = (id) => {
    const filteredCustomFonts = customFonts.filter((customFont) => customFont.id !== id);

    if (activeFontPairId === id) {
      onChangeFonts(sortedFonts[0].id);
    }

    setCustomFonts(filteredCustomFonts);
    updateSharedState({ customFonts: filteredCustomFonts });
  };

  const handleDragEnd = (_, { x, y }) => {
    setDialogPosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const observer = useMemo(() => {
    const root = document.querySelector(`.${DIALOG_CLASS_NAME}`);

    return new IntersectionObserver(
      (entries, obs) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) {
            const fontPair = fontsMap[target.id];

            if (fontPair.isCustom) {
              const compiledConfig = getCompiledFontPairConfig(fontPair);
              const { primaryFontFamily, secondaryFontFamily } = compiledConfig.variables;
              const usedFonts = Array.from(new Set([primaryFontFamily, secondaryFontFamily]));

              usedFonts.forEach((font) => {
                linkFont(font);
              });
            }

            preLoadFonts(target.id);
            obs.unobserve(target);
          }
        });
      },
      {
        root,
        rootMargin: '0px 0px 200px 0px',
      }
    );
  }, [preLoadFonts, fontsMap]);

  useEffect(() => () => observer.disconnect(), [observer]);

  return (
    <DesignFontsDialog
      isLoading={!isReady}
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      captions={captions}
      fonts={composeFontsAssetsPreviewUrl(sortedFonts)}
      fontsData={fontsData}
      customFonts={sortedCustomFonts}
      fontObserver={observer}
      activeFontId={activeFontPairId}
      {...(isAddCustomFontPairFeatureFlagEnabled && {
        onAddFontPairClick: readOnlyModeDecorator(onAddFontPairClick),
        onDeleteFontPairClick: readOnlyModeDecorator(onDeleteFontPairClick),
      })}
      {...(isAdvancedFontCustomizationFeatureFlagEnabled && {
        onEditFontPairClick: readOnlyModeDecorator(onEditFontPairClick),
      })}
      setActiveFont={readOnlyModeDecorator(onChangeFonts)}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onDragEnd={handleDragEnd}
    />
  );
};

DesignFontsContainer.propTypes = {
  sourceId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default DesignFontsContainer;
